import { useTranslation } from "react-i18next";
import { selectedLangSelector } from "../../../../services/i18n/redux/selectors";
import { useAppDispatch } from "../../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../../services/redux/hooks/use-selector";
import { getNumber, getProcessingTime } from "../helpers";
import {
  getAudioTranscriptionAction,
  postAudioFileAction,
} from "../redux/actions";
import { selectAudioTranscriptionInfo } from "../redux/selectors";
import {
  setAudioTranscriptionInfo,
  setTranscriptionModeAction,
} from "../redux/slice";
import type { AudioTranscriptionInfo } from "../types";

export const useAudioTranscription = () => {
  const dispatch = useAppDispatch();
  const audioTranscription = useAppSelector(selectAudioTranscriptionInfo);
  const { t } = useTranslation();
  const selectedLang = useAppSelector(selectedLangSelector);

  const uploadAudioFile = (file: File) => {
    let errorOccurred = false;
    dispatch(postAudioFileAction(file))
      .unwrap()
      .then((res) => {
        const processingTime = getProcessingTime(
          file.size,
          res.estimate_duration,
          selectedLang
        );
        const loadingInfo = `${t("audio_transcription.file_upload_message")}`;
        dispatch(
          setAudioTranscriptionInfo({
            hash: res.hash,
            progress: 0,
            status: "ready",
            text: `${loadingInfo} ${processingTime}`,
          })
        );
      })
      .catch(() => {
        errorOccurred = true;
        dispatch(setTranscriptionModeAction("error"));
      })
      .finally(() => {
        if (errorOccurred) {
          dispatch(setTranscriptionModeAction("error"));
        } else {
          dispatch(setTranscriptionModeAction("ready"));
        }
      });
  };

  const getProcessingResult = (signal: AbortSignal) => {
    const interval = setTimeout(function tick() {
      if (signal.aborted) {
        clearTimeout(interval);
        return;
      }
      dispatch(getAudioTranscriptionAction())
        .unwrap()
        .then((res) => {
          const { status, progress, text } = res;
          switch (status) {
            case "processing": {
              dispatch(
                setAudioTranscriptionInfo({
                  ...(audioTranscription as AudioTranscriptionInfo),
                  progress: getNumber(progress),
                  status: status,
                })
              );
              dispatch(setTranscriptionModeAction("processing"));
              setTimeout(tick, 3000);
              return;
            }
            case "completed": {
              dispatch(
                setAudioTranscriptionInfo({
                  ...(audioTranscription as AudioTranscriptionInfo),
                  progress: getNumber(progress),
                  text: text as string,
                  status: status,
                })
              );
              const timer = setTimeout(() => {
                dispatch(setTranscriptionModeAction("success"));
                clearTimeout(interval);
                clearTimeout(timer);
              }, 500);
              return;
            }
            case "failed": {
              dispatch(setTranscriptionModeAction("failed"));
              dispatch(
                setAudioTranscriptionInfo({
                  ...(audioTranscription as AudioTranscriptionInfo),
                  status: status,
                })
              );
              clearTimeout(interval);
              return;
            }
            default:
              return;
          }
        })
        .catch(() => {
          dispatch(setTranscriptionModeAction("failed"));
          clearTimeout(interval);
        });
    });
  };

  return {
    uploadAudioFile,
    getProcessingResult,
  };
};
